import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AngularSvgIconModule } from "angular-svg-icon";
import { Subscription } from "rxjs";

import { UserService } from "../../authentication/user.service";
import { PaymentsService } from "../../services/payments.service";
import { NovaInsightModalComponent } from "../nova-insight-modal/nova-insight-modal.component";
import { PillButtonComponent } from "../pill-button/pill-button.component";
import { NovaInsightService } from "../../services/nova-insight.service";
import { NovaInsightVisibilityService } from "../../services/nova-insight-visibility.service";
import { InsightCard } from "../../services/nova-insight.model";

/**
 * Nova's floating entry-point widget (FLIP-169) - the collapsed state of the Insight Card
 * feature, ported from the confirmed design handoff's `insight-card.html`/`.css`
 * (`.insightSnackbar`). Mounted once in app.component, always in the DOM, gated internally by
 * `showWidget` - the same shape as EmailConfirmationBannerComponent, which this is structurally
 * lifted from (fixed-position, always-mounted, persists until dismissed), rather than
 * SnackbarNewComponent/MatSnackBar (bottom-anchored, auto-dismissing toast) - Nova's widget
 * floats at the top and needs to persist like a modal, not disappear on a timer.
 *
 * Tapping it opens NovaInsightModalComponent (the expanded state) via MatDialog and dismisses
 * itself; the (x) button dismisses without opening the modal.
 */
@Component({
  selector: "app-nova-insight-widget",
  templateUrl: "./nova-insight-widget.component.html",
  styleUrls: ["./nova-insight-widget.component.scss"],
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule, PillButtonComponent],
})
export class NovaInsightWidgetComponent implements OnInit, OnDestroy {
  // FLIP-172: settings-eligible (on/off toggle + notification time reached) AND not dismissed,
  // per NovaInsightVisibilityService.showWidget$.
  settingsEligible = false;
  card: InsightCard | null = null;

  private subscription = new Subscription();
  // One fetch per session, and one "shown" record per card - both guarded here rather than by
  // where they're called from, since showWidget$ can emit true repeatedly (the 60s recheck).
  private cardRequested = false;
  private shownRecorded = false;

  constructor(
    private dialog: MatDialog,
    private userService: UserService,
    private paymentsService: PaymentsService,
    private novaInsightService: NovaInsightService,
    private visibilityService: NovaInsightVisibilityService
  ) {}

  ngOnInit(): void {
    // FLIP-202: the card is now generated server-side, so it arrives asynchronously. The
    // widget stays hidden until it lands (or stays hidden permanently if it doesn't) - the
    // `showWidget` getter already gates on `!!card`, so no extra loading state is needed.
    //
    // The fetch is deliberately driven off showWidget$ rather than run here directly. Two reasons:
    // this component is mounted once at bootstrap, before login, so an ngOnInit fetch 401s and
    // never retries (the user sees no Insight for the rest of the session); and generating a card
    // costs a Claude call, so it must not happen for a user whose settings say the widget won't be
    // shown at all.
    this.subscription.add(
      this.visibilityService.showWidget$.subscribe((showWidget) => {
        this.settingsEligible = showWidget;
        // FLIP-247: don't fetch/record a card for an account whose subscription has lapsed -
        // settingsEligible alone doesn't know about that, only showWidget below does.
        if (showWidget && this.paymentsService.isConfirmedActive) {
          this.ensureCardLoaded();
          this.maybeRecordShown();
        }
      })
    );

    // A second login in the same session (log out, log back in) must start clean - otherwise the
    // new user inherits the previous one's card and no fetch is made for their own.
    this.subscription.add(
      this.userService.userLoginStatusChanged$.subscribe((isLoggedIn) => {
        if (!isLoggedIn) {
          this.card = null;
          this.cardRequested = false;
          this.shownRecorded = false;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** Fetches the card the first time the widget is actually eligible to display one. */
  private ensureCardLoaded(): void {
    if (this.cardRequested) {
      return;
    }
    this.cardRequested = true;

    this.subscription.add(
      this.novaInsightService.getCurrentInsightCard().subscribe((card) => {
        this.card = card;
        // The card usually lands after eligibility, so this - not the showWidget$ handler - is
        // where the record normally fires.
        this.maybeRecordShown();
      })
    );
  }

  /**
   * Records the impression once the widget is genuinely on screen. Gated on `showWidget`, the same
   * getter the template renders on, so what the server learns matches what was displayed.
   *
   * The whole card is sent, not just its category: the server stores what the card said so the
   * Companion can refer back to it in conversation. `showWidget` already guarantees `card` is set.
   */
  private maybeRecordShown(): void {
    if (this.shownRecorded || !this.showWidget) {
      return;
    }
    this.shownRecorded = true;

    this.subscription.add(
      this.novaInsightService.recordInsightShown(this.card).subscribe()
    );
  }

  // FLIP-247: also requires a confirmed-active subscription, matching app.component's showNav
  // and NovaCompanionFabComponent.showFab - an already-onboarded user whose subscription has
  // lapsed is still logged in on /subscription-expired, so isUserLoggedIn() alone isn't enough.
  get showWidget(): boolean {
    return (
      this.userService.isUserLoggedIn() &&
      this.paymentsService.isConfirmedActive &&
      this.settingsEligible &&
      !!this.card
    );
  }

  /** (x) button - dismiss without opening the modal. */
  onDismiss(): void {
    this.visibilityService.dismiss();
  }

  /** Tapping the widget body - open the expanded Insight Card modal and dismiss the widget. */
  onOpen(): void {
    this.dialog.open(NovaInsightModalComponent, {
      data: this.card,
      panelClass: "base-modal-panel",
      backdropClass: "base-modal-backdrop",
    });
    this.visibilityService.dismiss();
  }
}
